import { useState, useEffect, useCallback } from "react";
import { adminApi, type PlanRow } from "@/lib/superadmin";
import {
  PageHeader,
  Card,
  ErrorBanner,
  EmptyState,
  PageSkeleton,
  GhostButton,
  PrimaryButton,
} from "../components/ui";

function inr(value: number): string {
  return `₹${value.toLocaleString("en-IN")}`;
}

/** Discount % when an original (MRP) price above the selling price is set. */
function discountPct(price: number, mrp?: number | null): number {
  if (!mrp || mrp <= price) return 0;
  return Math.round(((mrp - price) / mrp) * 100);
}

const EMPTY: PlanRow = { tier: "", label: "", price: 0, period: "year", rank: 0, active: true, features: [], mrp: null };

export default function PlansPage() {
  const [rows, setRows] = useState<PlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<PlanRow | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await adminApi.plans.list();
      setRows(r.plans.slice().sort((a, b) => a.rank - b.rank));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load plans.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openNew = () => {
    setEditing({ ...EMPTY, rank: rows.length + 1 });
    setIsNew(true);
  };
  const openEdit = (p: PlanRow) => {
    setEditing({ ...p });
    setIsNew(false);
  };

  const save = async () => {
    if (!editing || !editing.tier.trim() || !editing.label.trim()) return;
    setSaving(true);
    try {
      await adminApi.plans.upsert(editing);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tier: string) => {
    if (!confirm(`Delete plan "${tier}"? Tenants on this plan keep their subscription row but the plan disappears from the catalog.`)) return;
    try {
      await adminApi.plans.delete(tier);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed.");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      <PageHeader
        title="Plans & Pricing"
        subtitle="Create, price, and retire subscription plans"
        actions={
          <>
            <GhostButton onClick={() => void load()}>
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
              Refresh
            </GhostButton>
            <PrimaryButton onClick={openNew}>
              <i className="ri-add-line"></i>
              New plan
            </PrimaryButton>
          </>
        }
      />

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {loading ? (
        <PageSkeleton />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.length ? (
            rows.map((p, i) => (
              <Card
                key={p.tier}
                className={`flex flex-col hover:shadow-md transition-shadow ${
                  !p.active ? "opacity-70" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-foreground-400 font-semibold">
                      <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 normal-case">
                        <i className="ri-price-tag-3-line text-xs"></i>
                      </span>
                      {p.label}
                    </p>
                    <div className="flex items-end gap-2 mt-3">
                      <p className="text-3xl font-heading font-bold text-foreground-900">
                        {inr(Number(p.price))}
                      </p>
                      {discountPct(Number(p.price), p.mrp) > 0 && (
                        <span className="text-base text-foreground-400 line-through mb-1">{inr(Number(p.mrp))}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-foreground-500">per {p.period}</p>
                      {discountPct(Number(p.price), p.mrp) > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-600">
                          {discountPct(Number(p.price), p.mrp)}% OFF
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      p.active
                        ? "bg-emerald-50 text-emerald-600"
                        : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {p.active ? "Active" : "Inactive"}
                  </span>
                </div>

                {(p.features?.length ?? 0) > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {p.features!.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-xs text-foreground-600">
                        <i className="ri-check-line text-emerald-500 mt-0.5 shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <p className="text-[11px] font-mono text-foreground-400 mt-4">
                  tier: {p.tier} · rank {p.rank}
                </p>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-100">
                  <button
                    onClick={() => openEdit(p)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-pencil-line"></i>
                    Edit
                  </button>
                  <button
                    onClick={() => void remove(p.tier)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer ml-auto"
                  >
                    <i className="ri-delete-bin-line"></i>
                    Delete
                  </button>
                </div>
              </Card>
            ))
          ) : (
            <Card className="md:col-span-3" padded={false}>
              <EmptyState
                icon="ri-price-tag-3-line"
                title="No plans yet"
                hint="Create your first subscription plan to start monetizing."
                action={
                  <PrimaryButton onClick={openNew}>
                    <i className="ri-add-line"></i>
                    New plan
                  </PrimaryButton>
                }
              />
            </Card>
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 backdrop-blur-[2px] p-4" onClick={() => setEditing(null)}>
          <div className="bg-background-50 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-heading font-bold text-foreground-900">{isNew ? "New plan" : `Edit ${editing.label}`}</h2>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="text-foreground-600">Tier ID</span>
                <input
                  value={editing.tier}
                  disabled={!isNew}
                  onChange={(e) => setEditing({ ...editing, tier: e.target.value })}
                  placeholder="starter"
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 disabled:opacity-60"
                />
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Label</span>
                <input
                  value={editing.label}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  placeholder="Starter"
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Price (₹)</span>
                <input
                  type="number"
                  value={editing.price}
                  onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Original price (₹) — optional</span>
                <input
                  type="number"
                  value={editing.mrp ?? ""}
                  placeholder="leave blank for no discount"
                  onChange={(e) => setEditing({ ...editing, mrp: e.target.value === "" ? null : Number(e.target.value) })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
                {discountPct(editing.price, editing.mrp) > 0 && (
                  <span className="text-[11px] text-rose-600 font-semibold">Shows {inr(editing.mrp ?? 0)} struck through · {discountPct(editing.price, editing.mrp)}% OFF</span>
                )}
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Period</span>
                <select
                  value={editing.period}
                  onChange={(e) => setEditing({ ...editing, period: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 cursor-pointer"
                >
                  <option value="year">year</option>
                  <option value="month">month</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="text-foreground-600">Rank (order)</span>
                <input
                  type="number"
                  value={editing.rank}
                  onChange={(e) => setEditing({ ...editing, rank: Number(e.target.value) })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50"
                />
              </label>
              <label className="text-sm flex items-end gap-2 pb-2">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  className="w-4 h-4 cursor-pointer"
                />
                <span className="text-foreground-600">Active</span>
              </label>
            </div>
            <label className="text-sm block">
              <span className="text-foreground-600">Features (one per line)</span>
              <textarea
                value={(editing.features ?? []).join("\n")}
                onChange={(e) => setEditing({ ...editing, features: e.target.value.split("\n") })}
                rows={8}
                placeholder={"Everything in Starter\nWebsite + online ordering widget\nCaptain / waiter app"}
                className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm font-normal resize-y"
              />
              <span className="text-[11px] text-foreground-400">
                These bullets appear on the home pricing cards, /pricing, the in-app paywall and POS. Blank lines are ignored.
              </span>
            </label>
            <p className="text-[11px] text-foreground-400">
              Rank also drives tier comparison in Feature Control — higher rank unlocks more.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-foreground-600 hover:bg-background-100 rounded-md cursor-pointer">Cancel</button>
              <button
                onClick={() => void save()}
                disabled={saving || !editing.tier.trim() || !editing.label.trim()}
                className="px-4 py-2 text-sm font-semibold rounded-md bg-primary-600 text-background-50 hover:bg-primary-700 disabled:opacity-50 cursor-pointer"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
